import type { AuthModel } from '@develop/ia-api-library'
import { http } from '@develop/ia-api-library'
import { getDateDaysFromNow } from '@develop/ia-utils'
import { defineStore } from 'pinia'
import { SessionStorage } from '~/models'
import { IaCookie } from '@/models/cookie'
import type { CookieOptions } from '@/models/cookie'
import { SessionDuration } from '@/models/auth'
import type { AuthCookieModel } from '@/models/auth'

enum LogoutResult {
  Success = 1,
}

// TODO: could be turned into a composable
export const useAuthStore = defineStore('auth', () => {
  const checkoutStore = useCheckoutStore()
  const userStore = useUserStore()

  const { setCookie, deleteCookie } = useCookieManagerStore()

  // User validated
  const userValidated = useSessionStorage<boolean>(SessionStorage.ValidateUser, false)
  const setUserValidated = (value: boolean) => set(userValidated, value)

  // User logout callback
  const userLogoutCallback = useSessionStorage<LogoutResult | string | null>(SessionStorage.UserLogoutCallbackSession, null, { initOnMounted: true })
  const setUserLogoutCallback = (value: LogoutResult | string | null) => set(userLogoutCallback, value)
  const isUserLogoutCallbackSuccess = computed(() => userLogoutCallback.value === LogoutResult.Success)
  const isUserLogoutCallbackError = computed(() => isDefined(userLogoutCallback) && !isUserLogoutCallbackSuccess.value)

  const rememberMe = ref(false)
  const daysActive = computed(() => rememberMe.value
    ? SessionDuration.REMEMBER_ME
    : SessionDuration.DEFAULT,
  )

  const getCookieOptions = () => ({
    expires: getDateDaysFromNow(daysActive.value),
  } as CookieOptions)

  const bearer = useCookie<string | null>(IaCookie.Bearer, {
    // TODO: by setting watch to false, we can update cookie manually (and its options)
    // useCookie is then (i think) used only for reading cookie
    // and as a ref to keep store state and manually set (but does not update the cookie anymore)
    watch: false,
    ...getCookieOptions(),
  })
  const authData = useCookie<AuthCookieModel | null>(IaCookie.Authorization, {
    watch: false,
    ...getCookieOptions(),
  })

  const setCookies = (data: AuthModel) => {
    const options: CookieOptions = getCookieOptions()

    // have to set it both ways, so that we can update the cookie options as well
    set(bearer, data.token)
    setCookie({
      name: IaCookie.Bearer,
      value: data.token,
      options,
    })

    const fullAuthData: AuthCookieModel = { ...data, rememberMe: rememberMe.value }

    set(authData, fullAuthData)
    setCookie({
      name: IaCookie.Authorization,
      value: fullAuthData,
      options,
    })
  }

  const deleteCookies = () => {
    set(bearer, null)
    set(authData, null)
    deleteCookie(IaCookie.Bearer)
    deleteCookie(IaCookie.Authorization)
  }

  const setAuthData = async (data: AuthModel | undefined) => {
    if (!data) {
      setUserValidated(false)
      deleteCookies()
    } else {
      setCookies(data)
      setUserValidated(true)
    }

    // will fetch user or set it to null if no cookies are set
    await userStore.fetchUser()
  }

  const setRememberMe = (value: boolean) => set(rememberMe, value)

  const isAuthenticated = computed(() => isDefined(authData))
  const refreshToken = computed(() => authData.value?.refreshToken)
  const token = computed(() => authData.value?.token)

  const tokens = computed(() => {
    if (token.value && refreshToken.value) {
      return {
        token: token.value,
        refreshToken: refreshToken.value,
      }
    }
    return null
  })

  if (import.meta.client) {
    http.events.on('refresh_token', (data: AuthModel) => {
      if (data.token)
        setCookies(data)
    })
  }

  const clearData = async () => {
    try {
      onShowLogs(() => console.log('[logout]'))

      await setAuthData(undefined)
      setRememberMe(false)

      checkoutStore.reset()

      setUserLogoutCallback(LogoutResult.Success)
    } catch (err: any) {
      setUserLogoutCallback(err.message)
    }
  }

  return {
    isAuthenticated,
    refreshToken,
    token,
    tokens,
    rememberMe,
    setRememberMe,
    login: setAuthData,
    logout: clearData,
    userLogoutCallback,
    isUserLogoutCallbackSuccess,
    isUserLogoutCallbackError,
  }
})
